// Layer 4 (UI): the session cursor control — the TopBar clock is a button that
// opens an anchored popover with the steppers, so the bar gains no width
// (specvactual.B5 Q6). Reads/writes the session through the props only.

import { useEffect, useId, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RotateCcw,
} from 'lucide-react';
import { IconButton } from '@/design-system';
import { CURSOR_BOUNDS, DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import {
  addMinutes,
  diffMinutes,
  formatClock,
  formatClockShort,
  formatCursorBound,
} from '@/versions/v1/shared/clock.derive';

type SessionClockControlProps = {
  cursor: string;
  onStep: (minutes: number) => void;
  onReset: () => void;
};

const SHIFT = 12 * 60;

export function SessionClockControl({ cursor, onStep, onReset }: SessionClockControlProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const atDefault = cursor === DEFAULT_CURSOR;
  const canBackHour = diffMinutes(cursor, CURSOR_BOUNDS.min) > 0;
  const canFwdHour = diffMinutes(cursor, CURSOR_BOUNDS.max) < 0;
  const canBackShift = diffMinutes(addMinutes(cursor, -SHIFT), CURSOR_BOUNDS.min) >= 0;
  const canFwdShift = diffMinutes(addMinutes(cursor, SHIFT), CURSOR_BOUNDS.max) <= 0;
  const long = formatClock(cursor);

  useEffect(() => {
    if (!open) return;
    const close = () => {
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <span ref={rootRef} className="relative inline-flex min-w-0 items-center">
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex min-w-0 items-center gap-1 rounded-md px-1 py-0.5 tabular text-muted hover:bg-[var(--glass-surface)] hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] dark:hover:text-zinc-100"
        title={`${long} — session clock; click to move the cursor`}
        aria-label={`Session clock: ${long}. Open cursor controls`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">{formatClockShort(cursor)}</span>
        {!atDefault ? (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]"
            aria-label="Cursor moved from now"
          />
        ) : null}
        <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
      </button>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Session cursor"
          className="absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-2xl border border-divider bg-white p-3 shadow-lg dark:bg-zinc-950"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
            Session cursor
          </p>
          <p className="mt-1 tabular text-sm text-zinc-900 dark:text-zinc-100">{long}</p>
          <div className="mt-2 flex items-center justify-between gap-1">
            <IconButton
              type="button"
              label="Back one shift"
              disabled={!canBackShift}
              onClick={() => onStep(-SHIFT)}
            >
              <ChevronsLeft className="h-4 w-4" aria-hidden />
            </IconButton>
            <IconButton
              type="button"
              label="Back one hour"
              disabled={!canBackHour}
              onClick={() => onStep(-60)}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </IconButton>
            <IconButton
              type="button"
              label="Reset cursor to now"
              disabled={atDefault}
              onClick={onReset}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            </IconButton>
            <IconButton
              type="button"
              label="Forward one hour"
              disabled={!canFwdHour}
              onClick={() => onStep(60)}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </IconButton>
            <IconButton
              type="button"
              label="Forward one shift"
              disabled={!canFwdShift}
              onClick={() => onStep(SHIFT)}
            >
              <ChevronsRight className="h-4 w-4" aria-hidden />
            </IconButton>
          </div>
          <p className="mt-2 text-[11px] text-muted">
            Every value on the glass is the seeded series at this cursor · Cursor range{' '}
            {formatCursorBound(CURSOR_BOUNDS.min)} – {formatCursorBound(CURSOR_BOUNDS.max)}.
          </p>
        </div>
      ) : null}
    </span>
  );
}
